import { h, Component } from "preact";
import { connect } from "react-redux";
import { reduxForm, formValueSelector, change } from "redux-form";
import TicketsQuestions from "../routes/details/TicketsQuestions";
import OrderQuestions from "../routes/details/OrderQuestions";
import { getEvent } from "../state/actions/eventActions";
import {
  getOrder,
  redirectToComplete,
  saveAfterCheckoutQuestions
} from "../state/actions/orderActions";
import ActionBar from "./ActionBar";
import ActionButton from "./ActionButton";
import { scrollToErrors } from "../utils/ScrollToErrors";
import Smile from "../assets/images/logos/faces/simle";
import { css } from "emotion";

const styles = {
  mainContainer: {
    paddingTop: 40
  },
  messageContainer: css({
    textAlign: "center",
    maxWidth: 800,
    width: "100%",
    padding: "32px 134px",
    margin: "0px auto 20px auto",
    "@media(max-width: 600px)": {
      padding: "20px 14px"
    }
  }),
  questionContainer: css({
    maxWidth: 800,
    width: "100%",
    padding: "40px 180px",
    marginLeft: "auto",
    marginRight: "auto",
    "@media(max-width: 600px)": {
      padding: "20px 14px"
    }
  }),
  leftBtn: {
    marginRight: 5,
    marginLeft: 5,
    marginTop: 10,
    minWidth: 180
  },
  rightBtn: {
    marginRight: 5,
    marginLeft: 5,
    marginTop: 10,
    minWidth: 180
  }
};

/**
 * This is the form of after-checkout additional questions
 *
 * @param {object, object, function, function} { event, order, save, handleSubmit }
 * @returns form
 */
const formName = "afterCheckout";

class AfterCheckoutForm extends Component {
  constructor() {
    super();
    this.changeFieldValue = this.changeFieldValue.bind(this);
  }

  changeFieldValue(field, value) {
    const { dispatch } = this.props;
    dispatch(change(formName, field, value));
  }

  render() {
    const {
      event,
      order,
      save,
      handleSubmit,
      ticketsAdditionalQuestions = {},
      orderAdditionalQuestions = {},
      sameAsBuyerChecks = false,
      buyerData
    } = this.props;
    let detailsAdditionQuestions =
      event && event.data && event.data.additionalQuestions.length
        ? event.data.additionalQuestions.filter(q => q.askAfterCheckout)
        : [];
    detailsAdditionQuestions = detailsAdditionQuestions.map(q => {
      if (q.type === "multiSelect" && q.options && q.options.length) {
        q.options = q.options.map(o => {
          const newOption = o.label
            ? { ...o }
            : { ...o, id: o._id, label: o.value, value: false };
          return newOption;
        });
      }
      return q;
    });
    return (
      <form onSubmit={handleSubmit(save)}>
        <OrderQuestions
          additionalQuestions={detailsAdditionQuestions}
          orderAdditionalQuestions={orderAdditionalQuestions}
          tickets={order.data.tickets}
          formName={formName}
          changeFieldValue={this.changeFieldValue}
        />

        <TicketsQuestions
          tickets={order.data.tickets}
          ticketTypes={event.data.ticketTypes}
          additionalQuestions={detailsAdditionQuestions}
          ticketsAdditionalQuestions={ticketsAdditionalQuestions}
          formName={formName}
          changeFieldValue={this.changeFieldValue}
          sameAsBuyerChecks={sameAsBuyerChecks}
          buyerData={buyerData}
        />
        <ActionBar>
          <ActionButton>Submit</ActionButton>
        </ActionBar>
      </form>
    );
  }
}

/**
 * After payment, if there are after-checkout questions, this component is showed.
 *
 * @class PaymentSuccess
 * @extends {Component}
 */
class PaymentSuccess extends Component {
  constructor(props) {
    super(props);
  }

  save = values => {
    const { event, order, saveAfterCheckoutQuestions } = this.props;
    const orderData = {
      ...values.order,
      _id: order.data._id
    };
    saveAfterCheckoutQuestions(event, order, orderData, values.tickets);
  };

  render() {
    let {
      event = {},
      order,
      redirectToComplete,
      ticketsAdditionalQuestions = {},
      orderAdditionalQuestions = {},
      sameAsBuyerChecks,
      buyerData = {}
    } = this.props;
    const primaryColor =
      event.data.theme && event.data.theme.primaryColor
        ? event.data.theme.primaryColor
        : "#6621c0";

    if (!event) {
      return null;
    } else {
      return (
        <div style={styles.mainContainer}>
          <div className={`${styles.messageContainer} box`}>
            <Smile
              color={primaryColor}
              style={{ width: 60, marginBottom: 12 }}
            />
            <h1 style={styles.title}>Payment successful</h1>
            Thank you! Your order for {event.name} is complete.
            <br />
            <br />
            Please help us by filling out the additional attendee information
            below.
            <br />
            {event.data.settings.allowUserEdit ? (
              <span>
                You can also update this at any time through the link in your
                confirmation email.
              </span>
            ) : null}
            <div>
              <button
                className="btn btn-lg"
                style={styles.leftBtn}
                onClick={() =>
                  redirectToComplete(event.data.slug, event.data, order.data)
                }
                aria-label="Skip button"
              >
                Skip
              </button>
              <a href="#question" aria-label="View question button">
                <button
                  className="btn btn-lg btn-primary"
                  style={styles.rightBtn}
                  aria-label="View question button"
                  aria-hidden={true}
                >
                  View questions
                </button>
              </a>
            </div>
          </div>

          <div id="question" className={`${styles.questionContainer} box`}>
            <AfterCheckoutForm
              event={event.data}
              order={order.data}
              save={this.save}
              ticketsAdditionalQuestions={ticketsAdditionalQuestions}
              orderAdditionalQuestions={orderAdditionalQuestions}
              sameAsBuyerChecks={sameAsBuyerChecks}
              buyerData={buyerData}
            />
          </div>
        </div>
      );
    }
  }
}

const getTicketValues = (order, additionalQuestions, formTickets) => {
  if (formTickets) {
    return formTickets;
  }
  if (!order || !order.tickets || !order.tickets.length) {
    return null;
  }
  let mappedTicketValues = {};
  order.tickets.forEach(ticket => {
    mappedTicketValues[ticket._id] = {};
    // When users try to buy ticket, if default values exists, adds the values to details form
    additionalQuestions.forEach(question => {
      question.defaultValue && !question.perOrder && question.askAfterCheckout
        ? (mappedTicketValues[ticket._id][question._id] = question.defaultValue)
        : "";
    });
  });
  return mappedTicketValues;
};

const getOrderValues = (order, additionalQuestions, formOrder) => {
  if (formOrder) {
    return formOrder;
  }
  if (order.businessPurpose === false || !order.businessPurpose) {
    order.businessPurpose = "false";
  }
  if (order && order.additionalFields) {
    const mappedOrder = {
      ...order
    };
    mappedOrder.additionalFields = {};
    // When users try to buy ticket, if default values exists, adds the values to details form
    additionalQuestions.forEach(question => {
      question.defaultValue && question.perOrder && question.askAfterCheckout
        ? (mappedOrder.additionalFields[question._id] = question.defaultValue)
        : "";
    });

    return mappedOrder;
  }
};
AfterCheckoutForm = reduxForm({
  form: formName,
  destroyOnUnmount: false,

  onSubmitFail: errors => {
    scrollToErrors();
  }
})(AfterCheckoutForm);

AfterCheckoutForm = connect(
  state => {
    const order = getOrderValues(
      state.order.data,
      state.event.data.additionalQuestions,
      selector(state, "order")
    );
    const tickets = getTicketValues(
      state.order.data,
      state.event.data.additionalQuestions,
      selector(state, "tickets")
    );
    const initialValues = {
      order,
      tickets
    };

    return {
      initialValues: initialValues,
      event: state.event,
      order: state.order
    };
  },
  {}
)(AfterCheckoutForm);

const selector = formValueSelector(formName);

export default connect(
  (state, ownProps) => ({
    event: state.event,
    order: state.order,
    orderAdditionalQuestions: selector(state, "order.additionalFields"),
    ticketsAdditionalQuestions: selector(state, "tickets"),
    sameAsBuyerChecks: selector(state, "sameAsBuyerChecks"),
    buyerData: {
      firstName: state.order.data.firstName,
      lastName: state.order.data.lastName,
      mobile: state.order.data.mobile,
      email: state.order.data.email
    }
  }),
  { getEvent, getOrder, redirectToComplete, saveAfterCheckoutQuestions }
)(PaymentSuccess);



// WEBPACK FOOTER //
// ./components/PaymentSuccess.js